import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress
} from '@mui/material';
import { PrimaryButton, SecondaryButton } from '../../components';
import { GeneratedOption } from '../../types/fundamentos/FundamentosTypes';

interface SelectFundamentosModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (selectedOption: string) => void;
  options: GeneratedOption[];
  title: string;
  isLoading?: boolean;
}

const SelectFundamentosModal: React.FC<SelectFundamentosModalProps> = ({
  open,
  onClose,
  onSelect,
  options,
  title,
  isLoading = false
}) => {
  const [selectedValue, setSelectedValue] = React.useState<string>('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleConfirm = () => {
    if (selectedValue) {
      const selectedOption = options.find(opt => opt.id === selectedValue);
      if (selectedOption) {
        onSelect(selectedOption.text);
        setSelectedValue('');
        onClose();
      }
    }
  };

  const handleClose = () => {
    setSelectedValue('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        )}
        
        {!isLoading && options.length > 0 && (
          <>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Selecione a opção que melhor representa sua empresa:
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={selectedValue} onChange={handleChange}>
                {options.map((option) => (
                  <Box
                    key={option.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: selectedValue === option.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      backgroundColor: selectedValue === option.id ? 'action.selected' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <FormControlLabel
                      value={option.id}
                      control={<Radio />}
                      label={
                        <Typography variant="body1" sx={{ ml: 1 }}>
                          {option.text}
                        </Typography>
                      }
                      sx={{ margin: 0, width: '100%' }}
                    />
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </>
        )}
        
        {!isLoading && options.length === 0 && (
          <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
            Nenhuma opção foi gerada. Por favor, tente novamente.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton variant="contained" onClick={handleClose}>
          Cancelar
        </SecondaryButton>
        <PrimaryButton
          onClick={handleConfirm}
          disabled={!selectedValue || isLoading}
        >
          Selecionar
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default SelectFundamentosModal;
